import { type Command, CommandRing } from '../state/commandRing';
import {
  DEFAULT_INPUT_PROFILE,
  buildCommand,
  channelsFromAxes,
  type AuxState,
  type FlightMode,
  type InputProfile,
  type ResolvedChannels,
} from './channels';
import { GamepadSource } from './gamepad';
import { KeyboardSource } from './keyboard';

export type ActiveInputSource = 'gamepad' | 'keyboard';

export interface InputRouterOptions {
  commandRing: CommandRing;
  gamepadSource: GamepadSource;
  keyboardSource: KeyboardSource;
  profile?: InputProfile;
  onNotice?: (message: string) => void;
  mode?: FlightMode;
}

export interface InputDebugSnapshot {
  source: ActiveInputSource;
  connected: boolean;
  deviceId?: string;
  deviceHash?: string;
  rawAxes: number[];
  rawButtons: number[];
  channels: ResolvedChannels;
}

const inactiveAux = (): AuxState => ({
  arm: false,
  modeToggle: false,
  reset: false,
  releaseManual: false,
});

const idleChannels = (): ResolvedChannels => ({
  roll: 0,
  pitch: 0,
  yaw: 0,
  throttle: 0,
  aux: inactiveAux(),
});

const cloneChannels = (channels: ResolvedChannels): ResolvedChannels => ({
  ...channels,
  aux: { ...channels.aux },
});

/** Selects the live input source and emits one complete command per frame. */
export class InputRouter {
  private readonly commandRing: CommandRing;
  private readonly gamepadSource: GamepadSource;
  private readonly keyboardSource: KeyboardSource;
  private readonly profile: InputProfile;
  private readonly onNotice: (message: string) => void;

  private source: ActiveInputSource = 'keyboard';
  private mode: FlightMode;
  private previousAux = inactiveAux();
  private hasFramed = false;
  private debug: InputDebugSnapshot = {
    source: 'keyboard',
    connected: false,
    rawAxes: [],
    rawButtons: [],
    channels: idleChannels(),
  };

  constructor(options: InputRouterOptions) {
    this.commandRing = options.commandRing;
    this.gamepadSource = options.gamepadSource;
    this.keyboardSource = options.keyboardSource;
    this.profile = options.profile ?? DEFAULT_INPUT_PROFILE;
    this.onNotice = options.onNotice ?? (() => undefined);
    this.mode = options.mode ?? 'angle';
  }

  frame(dt: number): void {
    const gamepad = this.gamepadSource.poll();
    const nextSource: ActiveInputSource = gamepad.connected ? 'gamepad' : 'keyboard';
    if (this.hasFramed && nextSource !== this.source) {
      this.onNotice(
        nextSource === 'keyboard'
          ? 'gamepad lost - keyboard active'
          : 'gamepad connected - gamepad active',
      );
    }
    this.source = nextSource;

    let channels: ResolvedChannels;
    let rawAxes: number[];
    let rawButtons: number[];
    if (this.source === 'gamepad') {
      channels = channelsFromAxes(gamepad.axes, gamepad.buttons, this.profile);
      rawAxes = [...gamepad.axes];
      rawButtons = [...gamepad.buttons];
    } else {
      const keyboard = this.keyboardSource.sample(dt);
      channels = { ...keyboard.axes, aux: keyboard.aux };
      rawAxes = [
        keyboard.axes.roll,
        keyboard.axes.pitch,
        keyboard.axes.throttle,
        keyboard.axes.yaw,
      ];
      rawButtons = [
        Number(keyboard.aux.arm),
        Number(keyboard.aux.modeToggle),
        Number(keyboard.aux.reset),
        Number(keyboard.aux.releaseManual),
      ];
    }

    const arm = channels.aux.arm && !this.previousAux.arm;
    const disarm = !channels.aux.arm && this.previousAux.arm;
    const reset = channels.aux.reset && !this.previousAux.reset;
    const releaseManual = channels.aux.releaseManual && !this.previousAux.releaseManual;
    if (channels.aux.modeToggle && !this.previousAux.modeToggle) {
      this.mode = this.mode === 'angle' ? 'acro' : 'angle';
    }

    const edgedChannels: ResolvedChannels = {
      ...channels,
      aux: { ...channels.aux, arm, reset, releaseManual },
    };
    const command: Command = buildCommand(edgedChannels, this.mode);
    command.disarm = disarm;
    this.commandRing.push(command);

    this.previousAux = { ...channels.aux };
    this.debug = {
      source: this.source,
      connected: gamepad.connected,
      deviceId: gamepad.deviceId,
      deviceHash: gamepad.deviceHash,
      rawAxes,
      rawButtons,
      channels: cloneChannels(channels),
    };
    this.hasFramed = true;
  }

  activeSource(): ActiveInputSource {
    return this.source;
  }

  debugSnapshot(): InputDebugSnapshot {
    return {
      ...this.debug,
      rawAxes: [...this.debug.rawAxes],
      rawButtons: [...this.debug.rawButtons],
      channels: cloneChannels(this.debug.channels),
    };
  }
}
